import React from 'react';
import {
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
} from '@ant-design/icons';

import { FmBanner } from './FmBanner';
import { FmButton } from './FmButton';
import { FmInline, FmStack, FmText } from './FmComposition';
import { FmSlider } from './FmSlider';
import { useFmMapObservation } from './FmMapObservationContext';
import { formatTimestamp } from '@/components/Map/radarPlaybackShared';
import { useLanguage } from '@/i18n';
import './FmMapObservationControls.css';

export const FmMapObservationControls: React.FC = () => {
  const model = useFmMapObservation();
  const { language } = useLanguage();
  if (!model.enabled) return null;
  const timeRange =
    model.windowStart && model.windowEnd
      ? model.copy.timeRangeLabel
          .replace('{{start}}', formatTimestamp(model.windowStart, model.copy, language))
          .replace('{{end}}', formatTimestamp(model.windowEnd, model.copy, language))
      : null;

  return (
    <section
      className="fm-map-observation-controls"
      data-testid="radar-playback-control"
      aria-label={model.copy.ariaLabel}
    >
      <FmStack gap="sm">
        <FmInline align="center" justify="between" gap="sm">
          <FmText as="h3" size="caption">
            {model.copy.title}
          </FmText>
          <FmButton
            size="sm"
            variant="text"
            leadingIcon={<ReloadOutlined />}
            onClick={model.reload}
            aria-label={model.copy.reloadAria}
          />
        </FmInline>
        <FmText size="caption" tone="muted" data-testid="radar-active-timestamp">
          {formatTimestamp(model.activeFrame?.capturedAt, model.copy, language)}
        </FmText>
        {timeRange ? (
          <FmText size="caption" tone="muted" data-testid="radar-time-range">
            {timeRange}
          </FmText>
        ) : null}
        <FmInline as="fieldset" gap="xs" aria-label={model.copy.controlsAriaLabel}>
          <FmButton
            size="sm"
            variant="bordered"
            leadingIcon={<StepBackwardOutlined />}
            onClick={model.stepBackward}
            disabled={!model.canStep || model.activeIndex === 0}
            aria-label={model.copy.previousAria}
          />
          <FmButton
            size="sm"
            variant="bordered"
            leadingIcon={model.playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={() => model.setPlaying((value) => !value)}
            disabled={!model.canStep}
            aria-label={model.playing ? model.copy.pauseAria : model.copy.playAria}
          />
          <FmButton
            size="sm"
            variant="bordered"
            leadingIcon={<StepForwardOutlined />}
            onClick={model.stepForward}
            disabled={!model.canStep || model.activeIndex >= model.frameCount - 1}
            aria-label={model.copy.nextAria}
          />
        </FmInline>
        <FmSlider
          label={model.copy.opacityLabel}
          min={10}
          max={90}
          value={model.opacity}
          onChange={model.setOpacity}
          formatValue={(value) => `${value}%`}
          tone="inverse"
        />
        <output data-testid="radar-playback-status">
          {model.error ? (
            <FmBanner tone="error" title={model.statusText} />
          ) : model.noData || model.layerStatus ? (
            <FmBanner tone="info" title={model.statusText} />
          ) : (
            <FmText size="caption" tone="muted">
              {model.statusText}
            </FmText>
          )}
        </output>
      </FmStack>
    </section>
  );
};
